import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GraduationCap, Loader2, Play, ExternalLink, Info } from "lucide-react";
import { http } from "@/lib/api";

const DomainRow = ({ d, onPick }) => (
  <div data-testid={`college-domain-${d.number}`} className="card p-5 flex flex-col gap-3" style={d.cases.length ? undefined : { opacity: 0.6 }}>
    <div className="flex items-center justify-between">
      <span className="font-mono text-xs font-bold" style={{ color: "var(--accent)" }}>D{String(d.number).padStart(2, "0")}</span>
      <span className="text-xs" style={{ color: "var(--ink-3)" }}>{d.cases.length} college question{d.cases.length === 1 ? "" : "s"}</span>
    </div>
    <h3 className="font-display font-semibold leading-snug">{d.name}</h3>
    {d.cases.length === 0 ? (
      <p data-testid={`college-domain-empty-${d.number}`} className="text-xs mt-auto" style={{ color: "var(--ink-3)" }}>
        No college-written question for this domain in the ANZCA sample document.
      </p>
    ) : d.cases.map((c) => (
      <button key={c.id} data-testid={`college-start-${c.id}`} onClick={() => onPick(c.id)}
        className="btn-primary mt-auto h-10 px-4 rounded-full text-sm font-semibold flex items-center gap-2 justify-center">
        <Play size={14} /> Sample Viva {c.college_number}: {c.anzca_domain}
      </button>
    ))}
  </div>
);

export default function College() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => { http.get("/college").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin" /></div>;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <p className="font-mono text-xs tracking-[0.2em] uppercase" style={{ color: "var(--accent)" }}>College-written questions</p>
      <h1 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-3">
        <GraduationCap size={34} /> ANZCA sample vivas
      </h1>
      <div data-testid="college-source-note" className="mt-6 rounded-xl px-4 py-3 text-sm flex gap-3"
        style={{ background: "color-mix(in srgb, var(--accent) 10%, transparent)", border: "1px solid color-mix(in srgb, var(--accent) 35%, transparent)" }}>
        <Info size={18} className="shrink-0 mt-0.5" style={{ color: "var(--accent)" }} />
        <p>
          {data.note} Practise them here in the full exam format: 3-minute reading time, a 15-minute timer, hints, voice answers and feedback against ANZCA's own examiner expectations and marking rubric.{" "}
          <a data-testid="college-source-link" href={data.url} target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-1" style={{ color: "var(--accent)" }}>
            View the ANZCA document <ExternalLink size={12} />
          </a>
        </p>
      </div>
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="college-domain-grid">
        {data.domains.map((d) => <DomainRow key={d.number} d={d} onPick={(id) => nav(`/viva/${id}`)} />)}
      </div>
    </div>
  );
}
